import styled, { css } from "styled-components";
import { SubjectTone, toneColors } from "./subjectTone";

export const Page = styled.section`
    width: 100%;
    max-width: var(--container-max);
    margin: 56px auto 72px;
    padding: 0 var(--container-pad);

    @media (max-width: 600px) {
        margin: 28px auto 48px;
    }
`;

export const Head = styled.header`
    text-align: center;
    margin-bottom: 32px;
`;

export const Title = styled.h1`
    color: var(--color-primary);
    margin: 0;
    font-size: clamp(24px, 3.4vw, 38px);
    font-weight: 800;
    letter-spacing: -0.6px;
    line-height: 1.15;
`;

export const Subtitle = styled.p`
    display: inline-block;
    margin: 14px 0 0;
    padding: 6px 16px;
    border-radius: 999px;
    background: var(--color-primary-soft);
    color: var(--color-primary-dark);
    font-size: 14px;
    font-weight: 600;
`;

/* ---------------------------- сетка классов ---------------------------- */

export const GridScroll = styled.div`
    overflow-x: auto;
    padding-bottom: 8px;
    -webkit-overflow-scrolling: touch;

    @media (max-width: 760px) {
        display: none;
    }
`;

export const Grid = styled.div<{ $columns: number }>`
    display: grid;
    grid-template-columns: 118px repeat(${({ $columns }) => $columns}, minmax(140px, 1fr));
    gap: 10px;
    min-width: ${({ $columns }) => 118 + $columns * 150}px;
`;

export const HeadCell = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 2px;
    min-height: 82px;
    padding: 12px 10px;
    border-radius: var(--radius-md);
    background: linear-gradient(160deg, var(--color-primary) 0%, var(--color-primary-dark) 100%);
    color: #ffffff;
    text-align: center;
    box-shadow: var(--shadow-sm);
`;

export const HeadClass = styled.span`
    font-size: 16px;
    font-weight: 800;
    line-height: 1.2;
    letter-spacing: -0.2px;
    overflow-wrap: anywhere;
`;

export const HeadMeta = styled.span`
    font-size: 12px;
    font-weight: 600;
    opacity: 0.82;
`;

export const CornerCell = styled(HeadCell)`
    background: var(--color-bg-soft);
    color: var(--color-text-muted);
    border: 1px dashed var(--color-border);
    box-shadow: none;
    font-size: 12px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.6px;
`;

export const TimeCell = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 82px;
    padding: 10px;
    border-radius: var(--radius-md);
    background: var(--color-bg-soft);
    border: 1px solid var(--color-border);
    color: var(--color-text);
    font-size: 13px;
    font-weight: 700;
    text-align: center;
    white-space: nowrap;
`;

export const Cell = styled.div<{ $tone: SubjectTone }>`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 4px;
    min-height: 82px;
    padding: 12px 10px;
    border-radius: var(--radius-md);
    text-align: center;
    transition: transform var(--transition), box-shadow var(--transition);

    ${({ $tone }) => {
      const tone = toneColors[$tone];
      return css`
          background: ${tone.bg};
          border: 1px solid ${tone.border};
          color: ${tone.text};
      `;
    }}

    &:hover {
        transform: translateY(-2px);
        box-shadow: var(--shadow-sm);
    }
`;

export const EmptyCell = styled.div`
    min-height: 82px;
    border-radius: var(--radius-md);
    border: 1px dashed var(--color-border);
    background: repeating-linear-gradient(
        135deg,
        transparent,
        transparent 7px,
        rgba(227, 233, 214, 0.55) 7px,
        rgba(227, 233, 214, 0.55) 14px
    );
`;

export const Subject = styled.span`
    font-size: 14px;
    font-weight: 700;
    line-height: 1.25;
`;

export const SlotLabel = styled.span`
    font-size: 10px;
    font-weight: 800;
    text-transform: uppercase;
    letter-spacing: 0.8px;
    opacity: 0.7;
`;

export const CellTime = styled.span`
    font-size: 12px;
    font-weight: 600;
    opacity: 0.8;
    white-space: nowrap;
`;

export const CellNote = styled.span`
    padding: 2px 8px;
    border-radius: 999px;
    background: rgba(255, 255, 255, 0.7);
    font-size: 11px;
    font-weight: 700;
`;

/* ------------------------- мобильные карточки ------------------------- */

export const MobileList = styled.div`
    display: none;
    flex-direction: column;
    gap: 18px;

    @media (max-width: 760px) {
        display: flex;
    }
`;

export const MobileCard = styled.article`
    border-radius: var(--radius-lg);
    border: 1px solid var(--color-border);
    background: var(--color-bg);
    box-shadow: var(--shadow-sm);
    overflow: hidden;
`;

export const MobileCardHead = styled.div`
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 10px;
    padding: 14px 16px;
    background: linear-gradient(160deg, var(--color-primary) 0%, var(--color-primary-dark) 100%);
    color: #ffffff;
`;

export const MobileCardTitle = styled.span`
    font-size: 17px;
    font-weight: 800;
`;

export const MobileCardMeta = styled.span`
    font-size: 12px;
    font-weight: 600;
    opacity: 0.85;
`;

export const MobileRows = styled.ul`
    list-style: none;
    margin: 0;
    padding: 10px;
    display: flex;
    flex-direction: column;
    gap: 8px;
`;

export const MobileRow = styled.li<{ $tone: SubjectTone }>`
    display: grid;
    grid-template-columns: 96px 1fr;
    align-items: center;
    gap: 10px;
    padding: 10px 12px;
    border-radius: var(--radius-md);

    ${({ $tone }) => {
      const tone = toneColors[$tone];
      return css`
          background: ${tone.bg};
          border: 1px solid ${tone.border};
          color: ${tone.text};
      `;
    }}
`;

export const MobileTime = styled.span`
    font-size: 12px;
    font-weight: 800;
    white-space: nowrap;
`;

export const MobileCell = styled.span`
    display: flex;
    flex-direction: column;
    gap: 2px;
`;

export const MobileSubject = styled.span`
    font-size: 14px;
    font-weight: 700;
    line-height: 1.25;
`;

export const MobileMeta = styled.span`
    font-size: 11px;
    font-weight: 600;
    opacity: 0.75;
`;

export const MobileEmpty = styled.span`
    font-size: 13px;
    font-weight: 600;
    opacity: 0.6;
`;

export const Legend = styled.p`
    margin: 26px 0 0;
    text-align: center;
    font-size: 13px;
    color: var(--color-text-muted);
`;

/* ------------------------- кружки: строки слева ------------------------- */

export const ClubGrid = styled.div<{ $columns: number }>`
    display: grid;
    grid-template-columns: 186px repeat(${({ $columns }) => $columns}, minmax(152px, 1fr));
    gap: 10px;
    min-width: ${({ $columns }) => 186 + $columns * 162}px;
`;

export const ClubRowHead = styled.div`
    position: sticky;
    left: 0;
    z-index: 1;
    display: flex;
    flex-direction: column;
    justify-content: center;
    gap: 3px;
    min-height: 92px;
    padding: 14px 16px;
    border-radius: var(--radius-md);
    background: linear-gradient(160deg, var(--color-primary) 0%, var(--color-primary-dark) 100%);
    color: #ffffff;
    box-shadow: var(--shadow-sm);
`;

export const ClubName = styled.span`
    font-size: 16px;
    font-weight: 800;
    line-height: 1.2;
    letter-spacing: -0.2px;
`;

export const ClubPlace = styled.span`
    font-size: 12px;
    font-weight: 600;
    opacity: 0.82;
`;

export const ClubSlot = styled.div<{ $tone: SubjectTone }>`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 4px;
    min-height: 92px;
    padding: 12px 10px;
    border-radius: var(--radius-md);
    text-align: center;
    transition: transform var(--transition), box-shadow var(--transition);

    ${({ $tone }) => {
      const tone = toneColors[$tone];
      return css`
          background: ${tone.bg};
          border: 1px solid ${tone.border};
          color: ${tone.text};
      `;
    }}

    &:hover {
        transform: translateY(-2px);
        box-shadow: var(--shadow-sm);
    }
`;

export const SlotTime = styled.span`
    font-size: 15px;
    font-weight: 800;
    white-space: nowrap;
    letter-spacing: -0.2px;
`;

export const SlotGroup = styled.span`
    font-size: 12px;
    font-weight: 600;
    opacity: 0.85;
    line-height: 1.25;
`;
